import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Check, Minus, ArrowRight, CheckCircle2, Info, Sparkles } from 'lucide-react'
import { clsx } from 'clsx'
import { getComparisonData, type Cell } from '../data/comparisonData'
import { useSeoHead } from '../lib/seo'
import { pagePath } from '../lib/localizedUrl'

function CellMark({ cell }: { cell?: Cell }) {
  if (!cell) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />
  return (
    <div className="flex flex-col items-center gap-0.5">
      {cell.s === 'yes' && <Check className="w-5 h-5 text-green-600" />}
      {cell.s === 'partial' && <span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />}
      {cell.s === 'no' && <Minus className="w-4 h-4 text-gray-300" />}
      {cell.t && <span className="text-[10px] leading-tight text-gray-400 text-center max-w-[7rem]">{cell.t}</span>}
    </div>
  )
}

export default function ComparisonPage() {
  const { t, i18n } = useTranslation()
  const { COMPETITORS, COMPARE_ROWS, OUR_STRENGTHS, THEIR_STRENGTHS, COMPARE_VERIFIED } = getComparisonData(i18n.language)
  useSeoHead({
    title: t('comparison.seo_title'),
    description: t('comparison.seo_description'),
    hreflangPath: (l) => pagePath('comparison', l),
  })

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
          <Sparkles className="w-3.5 h-3.5" /> {t('comparison.badge')}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          {t('comparison.title')}
        </h1>
        <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
          {t('comparison.subtitle')}
        </p>
      </motion.div>

      {/* Ceny */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {COMPETITORS.map((c) => (
          <div key={c.key} className={clsx(
            'rounded-2xl border p-4 text-center',
            c.us ? 'border-primary-300 bg-primary-50/50 dark:bg-primary-500/10 ring-1 ring-primary-200' : 'border-gray-100 dark:border-white/10 bg-white dark:bg-white/5'
          )}>
            <p className={clsx('font-bold', c.us ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white')}>{c.name}</p>
            <p className="text-[11px] text-gray-400 mb-2">{c.origin}</p>
            <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{c.entryPrice}</p>
            <p className="text-[11px] text-gray-400">{t('comparison.per_month')}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 leading-tight">{c.freeTier}</p>
          </div>
        ))}
      </div>

      {/* Feature matice */}
      <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-white/10 mb-4 bg-white dark:bg-gray-900 shadow-sm">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-white/5">
              <th className="text-left font-semibold text-gray-700 dark:text-gray-200 px-4 py-3 w-[40%]">{t('comparison.feature_col')}</th>
              {COMPETITORS.map((c) => (
                <th key={c.key} className={clsx('px-3 py-3 text-center font-semibold',
                  c.us ? 'text-primary-700 dark:text-primary-300 bg-primary-50/60 dark:bg-primary-500/10' : 'text-gray-700 dark:text-gray-200')}>
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-white/5">
            {COMPARE_ROWS.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-white/5">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.feature}</td>
                {COMPETITORS.map((c) => (
                  <td key={c.key} className={clsx('px-3 py-3 align-middle', c.us && 'bg-primary-50/40 dark:bg-primary-500/5')}>
                    <CellMark cell={row.cells[c.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legenda + disclaimer */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500 mb-12">
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-600" /> {t('comparison.legend_full')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> {t('comparison.legend_partial')}</span>
        <span className="flex items-center gap-1.5"><Minus className="w-4 h-4 text-gray-300" /> {t('comparison.legend_none')}</span>
        <span className="flex items-center gap-1.5 ml-auto"><Info className="w-3.5 h-3.5" /> {t('comparison.disclaimer', { verified: COMPARE_VERIFIED })}</span>
      </div>

      {/* V čem jsme lepší */}
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-5">{t('comparison.our_strengths')}</h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-12">
        {OUR_STRENGTHS.map((s, i) => (
          <div key={i} className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{s.title}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Kde je konkurence napřed — poctivě */}
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('comparison.their_strengths')}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{t('comparison.their_strengths_sub')}</p>
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        {THEIR_STRENGTHS.map((s, i) => (
          <div key={i} className="bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5">
            <p className="font-semibold text-gray-900 dark:text-white">{s.title}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="rounded-3xl bg-gradient-to-br from-primary-600 to-cyan-500 px-6 py-10 text-center">
        <h2 className="text-2xl font-bold text-white mb-2">{t('comparison.cta_title')}</h2>
        <p className="text-white/90 mb-5 max-w-xl mx-auto">{t('comparison.cta_text')}</p>
        <Link to="/tools" className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition">
          {t('comparison.cta_button')} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  )
}
